import { format } from "date-fns"
import { de } from "date-fns/locale"
import { navigate } from "gatsby"

import {
  marketingClient,
  questionnaireClient,
  useMutation,
  useQueryClient,
} from "@trueskin-web/apis"
import { Button, Dialog, FieldError } from "@trueskin-web/components"
import { useAuth } from "@trueskin-web/context"
import { Routes, Trans, useTranslation } from "@trueskin-web/translations"

const QuestionnaireOverview = ({ data, isOpen, onDismiss }) => {
  const queryClient = useQueryClient()

  const { t } = useTranslation()

  const { user } = useAuth()

  const createdAt = format(new Date(user.createdAt), "dd. MMMM yyyy", {
    locale: de,
  })

  const {
    mutateAsync: postUsersSetRedoInitialQuestionnaire,
    isLoading,
    isError,
  } = useMutation(questionnaireClient.postUsersSetRedoInitialQuestionnaire, {
    onSuccess: async () => {
      await queryClient.refetchQueries("currentUser")

      onDismiss()

      navigate(Routes.App.Start.Questionnaire.url, {
        replace: true,
      })
    },
  })

  const handleClick = async () => {
    try {
      marketingClient.trackEvent({
        name: marketingClient.EVENT_TYPES_ENUM
          .RETAKE_INITIAL_QUESTIONNAIRE_CLICKED,
      })

      await postUsersSetRedoInitialQuestionnaire(user._id)
      /* eslint no-empty: ["error", { "allowEmptyCatch": true }] */
    } catch (err) {}
  }

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onDismiss={onDismiss}
        title={t("Start.EditQuestionnaire.QuestionnaireOverview.title")}
        dataTestId="edit-q-dialog"
        sx={{
          "&[data-reach-dialog-content]": {
            maxHeight: "100dvh",
            my: 0,
            px: 0,
            overflow: "hidden",
          },
          ".section-content": {
            position: ["absolute", "relative"],
            top: ["60px", "-16px"],
            bottom: 0,
            height: `calc(100dvh - 76px)`,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            background: "background",
          },
          ".section-header": {
            pr: 4,
            pb: 4,
            borderBottom: "1px",
            borderColor: "lightGrey",
          },
          h2: {
            px: 4,
            fontSize: 2,
            fontWeight: "bold",
            lineHeight: "body",
          },
        }}
      >
        <div
          sx={{
            pt: 2,
            px: 4,
            overflowY: "auto",
          }}
        >
          <div
            sx={{
              fontSize: 0,
              color: "textDark",
            }}
            data-testid="edit-q-dialog-date"
          >
            <Trans
              i18nKey="Start.EditQuestionnaire.QuestionnaireOverview.subline"
              values={{
                createdAt,
              }}
            />
          </div>
          {data.map(({ question, answer }, index) => (
            <div
              key={index}
              sx={{
                py: 5,
                borderBottom: "1px",
                borderColor: "lightGray",
                "&:last-of-type": {
                  borderBottom: "none",
                },
              }}
              data-testid="edit-q-dialog-item"
            >
              <div
                sx={{ mb: 2, color: "textDark" }}
                data-testid="edit-q-dialog-item-question"
              >
                {question}
              </div>
              <div
                sx={{ fontSize: 3, fontWeight: "bold" }}
                data-testid="edit-q-dialog-item-answer"
              >
                {answer}
              </div>
            </div>
          ))}
        </div>
        <div
          sx={{
            py: 3,
            px: 4,
            borderTop: "1px",
            borderColor: "grey",
          }}
        >
          {isError && (
            <FieldError sx={{ mb: 4 }}>
              <Trans
                i18nKey="Start.EditQuestionnaire.QuestionnaireOverview.errorMessage"
                values={{
                  createdAt,
                }}
              />
            </FieldError>
          )}
          <Button
            isFullWidth
            rounded
            onClick={handleClick}
            isLoading={isLoading}
            data-testid="edit-q-dialog-submit-button"
          >
            {t("Start.EditQuestionnaire.QuestionnaireOverview.buttonText")}
          </Button>
        </div>
      </Dialog>
    </>
  )
}

export default QuestionnaireOverview
